import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { UserAccount, UserAccountRequest } from './user.model';
import { Bank } from '../holl-bank-management/bank.model';
import { UserService } from './user.service';
import { BankService } from '../holl-bank-management/bank.service';
import { ExportService } from '../service/quote-export.service';
import { DashboardService } from '../service/dashboard.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-holl-user-management',
  templateUrl: './holl-user-management.component.html',
  styleUrls: ['./holl-user-management.component.scss']
})
export class HollUserManagementComponent implements OnInit {

  users: UserAccount[] = [];
  filteredUsers: UserAccount[] = [];
  searchQuery = '';
  isLoading = false;

  // ── Export dropdown ────────────────────────────────────
  showExportMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const wrap = this.elRef.nativeElement.querySelector('.export-wrap');
    if (this.showExportMenu && wrap && !wrap.contains(event.target)) {
      this.showExportMenu = false;
    }
  }
  selectedUser: UserAccount | null = null;
  viewMode: 'list' | 'detail' | 'form' = 'list';
  isEditing = false;

  // Pagination
  currentPage = 0;
  pageSize = 5;
  totalItems = 0;
  totalPages = 0;

  // Filter
  userTypes: string[] = [];
  selectedTypeFilter = '';

  // Banks list for dropdown
  banks: Bank[] = [];

  // Form model
  userForm: UserAccountRequest = this.getEmptyForm();

  // Validation state
  formErrors: { [key: string]: string } = {};
  formTouched: { [key: string]: boolean } = {};

  // Password visibility
  showPassword = false;

  constructor(
    private router: Router,
    private userService: UserService,
    private bankService: BankService,
    private exportService: ExportService,
    private elRef: ElementRef,
    private dashboardService: DashboardService
  ) {}

  ngOnInit() {
    this.loadUsers();
    this.loadUserTypes();
    this.loadBanks();
  }

  /**
   * True when the logged-in user is a super admin —
   * i.e. their agency (bank_id) is null or equals 10.
   */
  get isSuperAdmin(): boolean {
    const u = this.dashboardService.currentUser();
    return !u || u.agency === null || u.agency === undefined || u.agency === 10;
  }

  getEmptyForm(): UserAccountRequest {
    return {
      fullName: '',
      email: '',
      phone: '',
      password: '',
      userType: '',
      bankId: null
    };
  }

  // ── Load users from API ───────────────────────────────────
  loadUsers() {
    this.isLoading = true;

    this.userService.getAllUsers(this.currentPage, this.pageSize, null, this.selectedTypeFilter || null).subscribe(
      (response) => {
        if (response.responseCode === '000') {
          this.users = response.data.users || [];
          this.totalItems = response.data.totalItems;
          this.totalPages = response.data.totalPages;
          this.currentPage = response.data.currentPage;
          this.filteredUsers = this.users.slice();
        } else {
          Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to load users.', icon: 'error' });
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading users:', error);
        Swal.fire({ title: 'Error', text: 'Unable to connect to the server. Please try again later.', icon: 'error' });
        this.isLoading = false;
      }
    );
  }

  loadUserTypes() {
    this.userService.getUserTypes().subscribe(
      (types) => { this.userTypes = types || []; },
      (error) => { console.error('Error loading user types:', error); }
    );
  }

  loadBanks() {
    this.bankService.getAllBanks(0, 100).subscribe(
      (response) => {
        if (response.responseCode === '000') {
          this.banks = response.data.banks || [];
        }
      },
      (error) => { console.error('Error loading banks:', error); }
    );
  }

  // ── Search (client-side filter on loaded page) ────────────
  onSearch() {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredUsers = this.users.slice();
      return;
    }
    this.filteredUsers = this.users.filter(u =>
      u.fullName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.phone.includes(q) ||
      (u.userType && u.userType.toLowerCase().includes(q)) ||
      (u.bank && u.bank.bankName && u.bank.bankName.toLowerCase().includes(q))
    );
  }

  onTypeFilterChange() {
    this.currentPage = 0;
    this.loadUsers();
  }

  // ── View user detail ──────────────────────────────────────
  viewUser(user: UserAccount) {
    this.selectedUser = Object.assign({}, user);
    this.viewMode = 'detail';
  }

  // ── Open form for creating ────────────────────────────────
  openCreateForm() {
    this.userForm = this.getEmptyForm();
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = false;
    this.viewMode = 'form';
  }

  // ── Open form for editing ─────────────────────────────────
  openEditForm(user: UserAccount) {
    // bank_id=10 is the backend sentinel for "super admin / no specific bank" — treat as null in the form
    const resolvedBankId = user.bank && user.bank.bankID !== 10 ? user.bank.bankID : null;
    this.userForm = {
      fullName: user.fullName,
      email: user.email,
      phone: user.phone,
      password: '',
      userType: user.userType,
      bankId: resolvedBankId
    };
    this.selectedUser = user;
    this.formErrors = {};
    this.formTouched = {};
    this.isEditing = true;
    this.viewMode = 'form';
  }

  // ── Back to list ──────────────────────────────────────────
  backToList() {
    this.viewMode = 'list';
    this.selectedUser = null;
  }

  // ── Validation ────────────────────────────────────────────
  markTouched(field: string) {
    this.formTouched[field] = true;
    this.validateField(field);
  }

  validateField(field: string) {
    const val = this.userForm[field];

    switch (field) {
      case 'fullName':
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Full name is required.';
        } else if (val.trim().length < 2) {
          this.formErrors[field] = 'Full name must be at least 2 characters.';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'email':
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Email address is required.';
        } else if (!emailRegex.test(val.trim())) {
          this.formErrors[field] = 'Please enter a valid email address.';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'phone':
        const phoneRegex = /^\+?[0-9]{10,20}$/;
        if (!val || !val.trim()) {
          this.formErrors[field] = 'Phone number is required.';
        } else if (!phoneRegex.test(val.trim().replace(/\s/g, ''))) {
          this.formErrors[field] = 'Please enter a valid phone number (e.g. +233501234567).';
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'password':
        if (!this.isEditing) {
          if (!val || !val.trim()) {
            this.formErrors[field] = 'Password is required.';
          } else if (val.length < 8) {
            this.formErrors[field] = 'Password must be at least 8 characters.';
          } else {
            delete this.formErrors[field];
          }
        } else {
          delete this.formErrors[field];
        }
        break;

      case 'userType':
        if (!val || !val.trim()) {
          this.formErrors[field] = 'User type is required.';
        } else {
          delete this.formErrors[field];
        }
        // Re-evaluate bank requirement whenever user type changes
        if (this.formTouched['bankId']) {
          this.validateField('bankId');
        }
        break;

      case 'bankId':
        // bankId is required when userType is AGENT
        if (this.userForm.userType === 'AGENT' && !this.userForm.bankId) {
          this.formErrors[field] = 'Bank is required for Agent users.';
        } else {
          delete this.formErrors[field];
        }
        break;
    }
  }

  validateAll(): boolean {
    const fields = ['fullName', 'email', 'phone', 'password', 'userType', 'bankId'];
    fields.forEach(f => {
      this.formTouched[f] = true;
      this.validateField(f);
    });
    return Object.keys(this.formErrors).length === 0;
  }

  hasError(field: string): boolean {
    return this.formTouched[field] && !!this.formErrors[field];
  }

  // ── Save user (Create / Update via API) ───────────────────
  saveUser() {
    if (!this.validateAll()) {
      return;
    }

    this.isLoading = true;

    if (this.isEditing) {
      this.userService.updateUser(this.selectedUser.userID, this.userForm).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({ title: 'Success!', text: '"' + this.userForm.fullName + '" has been updated successfully.', icon: 'success' });
            this.viewMode = 'list';
            this.loadUsers();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to update user.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error updating user:', error);
          Swal.fire({ title: 'Error', text: 'Unable to update user. Please try again.', icon: 'error' });
        }
      );
    } else {
      this.userService.createUser(this.userForm).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.responseCode === '000') {
            Swal.fire({
              title: 'User Created!',
              text: '"' + this.userForm.fullName + '" has been registered. A welcome email has been sent to ' + this.userForm.email + '.',
              icon: 'success'
            });
            this.viewMode = 'list';
            this.loadUsers();
          } else {
            Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to create user.', icon: 'error' });
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Error creating user:', error);
          Swal.fire({ title: 'Error', text: 'Unable to create user. Please try again.', icon: 'error' });
        }
      );
    }
  }

  // ── Delete user via API ───────────────────────────────────
  deleteUser(user: UserAccount) {
    Swal.fire({
      title: 'Delete User?',
      text: `Are you sure you want to delete "${user.fullName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F05B24',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.isLoading = true;

        this.userService.deleteUser(user.userID).subscribe(
          (response) => {
            this.isLoading = false;
            if (response.responseCode === '000') {
              if (this.selectedUser && this.selectedUser.userID === user.userID) {
                this.viewMode = 'list';
                this.selectedUser = null;
              }
              Swal.fire({ title: 'Deleted!', text: `"${user.fullName}" has been removed.`, icon: 'success' });
              this.loadUsers();
            } else {
              Swal.fire({ title: 'Error', text: response.responseMessage || 'Failed to delete user.', icon: 'error' });
            }
          },
          (error) => {
            this.isLoading = false;
            console.error('Error deleting user:', error);
            Swal.fire({ title: 'Error', text: 'Unable to delete user. Please try again.', icon: 'error' });
          }
        );
      }
    });
  }

  // ── Pagination ─────────────────────────────────────────────
  goToPage(page: number) {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadUsers();
  }

  nextPage() {
    this.goToPage(this.currentPage + 1);
  }

  prevPage() {
    this.goToPage(this.currentPage - 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible);
    if (end - start < maxVisible) {
      start = Math.max(0, end - maxVisible);
    }
    for (let i = start; i < end; i++) {
      pages.push(i);
    }
    return pages;
  }

  // ── Export ────────────────────────────────────────────────
  toggleExportMenu() { this.showExportMenu = !this.showExportMenu; }

  exportAs(format: 'excel' | 'csv' | 'pdf') {
    this.showExportMenu = false;
    const data = this.filteredUsers.length ? this.filteredUsers : this.users;
    if (!data || data.length === 0) { return; }
    const filename = 'user_management_' + new Date().toISOString().slice(0, 10);
    this.exportService.export(format, ExportService.USERS, data, filename);
  }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }

  formatDate(dateStr: string, includeTime: boolean = false): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (!includeTime) return datePart;
    const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }

  getUserTypeBadgeClass(userType: string): string {
    switch ((userType || '').toUpperCase()) {
      case 'ADMIN':  return 'badge-admin';
      case 'AGENT':  return 'badge-agent';
      case 'BROKER': return 'badge-broker';
      case 'CLIENT': return 'badge-client';
      default:       return 'badge-default';
    }
  }

  getStatusClass(status: string): string {
    return (status || '').toLowerCase() === 'active' ? 'status-active' : 'status-inactive';
  }

  /**
   * Returns the display label for a user's bank.
   * bank_id=10 (or null bank) is the backend sentinel for "super admin / no specific bank".
   */
  getBankDisplay(user: UserAccount): string {
    if (!user.bank || user.bank.bankID === 10) {
      return 'Super Admin';
    }
    return user.bank.bankName || '—';
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }
}
